import { QUALITY_CHECK } from './served-lines'

/**
 * When Otvorené stoly rings the kitchen's bell - FRONTEND.md §9.675, owner O56, a missed port.
 *
 * <p><b>Legacy's rule, exactly</b> (`restaurant.tsx`): an order rings when its status is `quality_check` - the
 * kitchen's *Hotová* - and on the previous poll it was something else. `prev && prev !== "quality_check"`: an order
 * the screen had **not** seen before does not ring, because the screen cannot know it only just turned ready, and
 * one that was already ready does not ring again on every poll.
 *
 * <p><b>A second round rings again.</b> A kitchen round on a finished order writes `status = 'new'`
 * (`OrderUpdateService.addRound`), so the next *Hotová* is a second move into `quality_check` - which is the waiter's
 * second plate to carry.
 *
 * <p><b>Nothing is remembered before the screen's read has answered</b> - the caller's half, and FB-997's lesson
 * (§9.677): the render before the first answer has no orders, and remembering *no orders* there would make every
 * order the first answer brings look new rather than unknown.
 */

/** Each order's status on the last answered read, by id. */
export type StatusMemory = ReadonlyMap<number, string>

interface StatusedOrder {
  id?: number
  status?: string | null
}

/** What to remember about these orders for the next read. */
export function statusMemoryOf(orders: readonly StatusedOrder[]): Map<number, string> {
  const memory = new Map<number, string>()
  for (const order of orders) {
    if (order.id === undefined) continue
    memory.set(order.id, order.status ?? '')
  }
  return memory
}

/**
 * The ids that have just turned ready, given what the last answered read said. Empty when there was no last read.
 *
 * <p>The screen rings **once** for any number of them, as legacy does: two bells over each other is noise, and the
 * amber pulse on each card (§9.443) already says which tables.
 */
export function turnedReady(
  previous: StatusMemory | null,
  orders: readonly StatusedOrder[],
): number[] {
  if (previous === null) return []
  const ready: number[] = []
  for (const order of orders) {
    if (order.id === undefined || order.status !== QUALITY_CHECK) continue
    const before = previous.get(order.id)
    if (before !== undefined && before !== QUALITY_CHECK) ready.push(order.id)
  }
  return ready
}
